/**
 * Cliente das rotas `/api/sfu/*` — que sao um proxy pro Cloudflare Realtime.
 * O App Secret nunca chega aqui; o navegador se identifica com o token que
 * recebeu ao entrar na sala.
 */

export interface SfuSession {
    sessionId: string
    iceServers?: RTCIceServer[]
}

export interface SfuTrackRequest {
    location: 'local' | 'remote'
    mid?: string
    trackName: string
    sessionId?: string
}

export interface SfuTracksResponse {
    requiresImmediateRenegotiation?: boolean
    sessionDescription?: RTCSessionDescriptionInit
    tracks?: Array<{ mid?: string; trackName?: string; error?: string; errorDescription?: string }>
    errorCode?: string
    errorDescription?: string
}

/** Trilha a fechar: a Cloudflare fecha por `mid`; o nome so serve a conta do servidor. */
export interface SfuTrackRef {
    mid: string
    trackName?: string
}

export class SfuError extends Error {
    constructor(message: string, readonly status?: number) {
        super(message)
        this.name = 'SfuError'
    }
}

export interface SfuApi {
    createSession(): Promise<SfuSession>
    newTracks(sessionId: string, body: unknown): Promise<SfuTracksResponse>
    renegotiate(sessionId: string, description: RTCSessionDescriptionInit): Promise<unknown>
    /**
     * Fecha a sessao e devolve a vaga dela na cota do servidor. Lista vazia
     * vale pra sessao que nunca chegou a ter trilha.
     */
    closeTracks(sessionId: string, tracks: SfuTrackRef[]): Promise<unknown>
}

export function createSfuApi(getToken: () => string | null): SfuApi {
    async function request<T>(method: 'POST' | 'PUT', path: string, body?: unknown): Promise<T> {
        const token = getToken()
        if (!token) throw new SfuError('sem token de sessao')

        const response = await fetch(path, {
            method,
            headers: {
                'content-type': 'application/json',
                'x-session-token': token
            },
            body: body === undefined ? undefined : JSON.stringify(body)
        })

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
            const detail = (payload as { errorDescription?: string; error?: string })
            throw new SfuError(detail.errorDescription ?? detail.error ?? `HTTP ${response.status}`, response.status)
        }
        return payload as T
    }

    return {
        createSession: () => request<SfuSession>('POST', '/api/sfu/sessions'),
        newTracks: (sessionId, body) =>
            request<SfuTracksResponse>('POST', `/api/sfu/sessions/${encodeURIComponent(sessionId)}/tracks`, body),
        renegotiate: (sessionId, description) =>
            request('PUT', `/api/sfu/sessions/${encodeURIComponent(sessionId)}/renegotiate`, {
                sessionDescription: { type: description.type, sdp: description.sdp }
            }),
        closeTracks: (sessionId, tracks) =>
            request('PUT', `/api/sfu/sessions/${encodeURIComponent(sessionId)}/tracks-close`, {
                // Sem o `mid` a Cloudflare recusava o fechamento, e a vaga nunca voltava.
                tracks: tracks.map(({ mid, trackName }) => ({ mid, ...(trackName ? { trackName } : {}) })),
                force: true
            })
    }
}

/**
 * A Cloudflare responde a offer inicial ja com os candidatos dela (ICE lite),
 * mas o SDP que sobe precisa estar completo. Espera o gathering — com teto,
 * porque alguns navegadores demoram atras de proxy.
 */
export function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 3000): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve()
    return new Promise((resolve) => {
        const done = () => {
            pc.removeEventListener('icegatheringstatechange', onChange)
            clearTimeout(timer)
            resolve()
        }
        const onChange = () => {
            if (pc.iceGatheringState === 'complete') done()
        }
        const timer = setTimeout(done, timeoutMs)
        pc.addEventListener('icegatheringstatechange', onChange)
    })
}
